import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  directoryApi,
  type EmailConnection,
  type OrganisationEmailConnections,
  type StartEmailConnectionRequest,
  type StartEmailConnectionResponse,
  type UpdateEmailConnectionRequest,
} from '../client';

/** Stable query-key factory for the organisation's connected mailboxes. */
export const organisationEmailConnectionKeys = {
  all: ['directory', 'organisation-email-connection'] as const,
  byOrganisation: (organisationId: string) =>
    ['directory', 'organisation-email-connection', 'org', organisationId] as const,
};

/** Connected mailboxes, plus whether I may manage them. `null` keeps it disabled. */
export function useOrganisationEmailConnections(organisationId: string | null) {
  return useQuery<OrganisationEmailConnections, Error>({
    queryKey: organisationId
      ? organisationEmailConnectionKeys.byOrganisation(organisationId)
      : organisationEmailConnectionKeys.all,
    queryFn: () => {
      if (!organisationId) throw new Error('No organisation id');
      return directoryApi.getOrganisationEmailConnections(organisationId);
    },
    enabled: !!organisationId,
  });
}

/**
 * Starts a Microsoft / Google sign-in. The caller sends the browser to the
 * returned `authorizationUrl`; Directory brings it back to `returnUrl`.
 */
export function useStartEmailConnection() {
  return useMutation<StartEmailConnectionResponse, Error, StartEmailConnectionRequest>({
    mutationKey: [...organisationEmailConnectionKeys.all, 'start'],
    mutationFn: (body) => directoryApi.startEmailConnection(body),
  });
}

/** Refreshes the organisation's list after any change to one connection. */
function useInvalidateOnSuccess() {
  const qc = useQueryClient();
  return (connection: { organisationId: string }) =>
    qc.invalidateQueries({
      queryKey: organisationEmailConnectionKeys.byOrganisation(connection.organisationId),
    });
}

export function useUpdateEmailConnection() {
  const invalidate = useInvalidateOnSuccess();
  return useMutation<EmailConnection, Error, UpdateEmailConnectionRequest>({
    mutationKey: [...organisationEmailConnectionKeys.all, 'update'],
    mutationFn: (body) => directoryApi.updateEmailConnection(body),
    onSuccess: (connection) => invalidate(connection),
  });
}

/**
 * Sends the test email. Refreshes the list whether it worked or not — a
 * failure records its reason, or flips the connection to "needs reconnecting".
 */
export function useSendEmailConnectionTest() {
  const invalidate = useInvalidateOnSuccess();
  return useMutation<EmailConnection, Error, { organisationId: string; id: string }>({
    mutationKey: [...organisationEmailConnectionKeys.all, 'test'],
    mutationFn: ({ id }) => directoryApi.sendEmailConnectionTest(id),
    onSettled: (_data, _error, vars) => invalidate(vars),
  });
}

export function useDeleteEmailConnection() {
  const invalidate = useInvalidateOnSuccess();
  return useMutation<unknown, Error, { organisationId: string; id: string }>({
    mutationKey: [...organisationEmailConnectionKeys.all, 'delete'],
    mutationFn: ({ id }) => directoryApi.deleteEmailConnection(id),
    onSuccess: (_data, vars) => invalidate(vars),
  });
}
